import _ from 'lodash';

import React, { lazy, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useLanguage, useListing, useListingVideoTemplates, useAuth, useAlert } from 'hooks';
import { HvButton } from 'components';
import { hideModal } from 'state/actions';
import { images } from 'assets/images';
import { VideoTemplate, Picture } from 'models';
import { SLIDESHOWMODALSTEP, ENG, FR, COLOR, ALERT, MODAL } from 'config/constants';

const ReactPlayer = lazy(() => {
  return import('react-player');
});
const useStyles = makeStyles({
  slideshowModalStepper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  backButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  title: {
    textAlign: 'center',
    fontSize: 16,
    fontFamily: 'FordAntenna-Medium',
    marginBottom: 10,
  },
  subtitle: {
    color: COLOR.DARK_GREY,
    fontWeight: 'bold',
  },
  reminderContent: {
    border: `1px solid ${COLOR.LIGHT_BLUE}`,
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
  },
  reminderText: {
    color: COLOR.LIGHT_BLUE,
    fontSize: 12,
  },
  infoContent: {
    marginBottom: 20,
  },
  videoPlayer: {
    marginBottom: 10,
    position: 'relative',
    width: '100% !important',
    height: 'auto !important',

    '& video': {
      maxHeight: 300,
    },
  },
  logo: {
    objectFit: 'cover',
  },
});
interface Props {
  setActiveStep: (_: number) => void;
  selectedTemplate: VideoTemplate;
  picturesWithNewOrder: Picture[];
}

function SlideshowSummary({ setActiveStep, selectedTemplate, picturesWithNewOrder }: Props) {
  const styles = useStyles();
  const intl = useIntl();
  const language = useLanguage();
  const dispatch = useDispatch();
  const { displayAlert } = useAlert();
  const theme_lang = language === ENG ? 'en' : FR;
  const { token } = useAuth();
  const path = window.location.pathname;
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { listing } = useListing(listing_token);
  const { broker } = listing;
  const { centrisData } = broker;
  const logo_url = centrisData && centrisData.photo_url ? centrisData.photo_url : broker.logo_url;
  const firstname = centrisData && centrisData.firstname ? centrisData.firstname : broker.firstname;
  const lastname = centrisData && centrisData.lastname ? centrisData.lastname : broker.lastname;
  const email = centrisData && centrisData.email ? centrisData.email : broker.email;
  const phone = centrisData && centrisData.main_phone ? centrisData.main_phone : broker.phone;
  const { orderListingVideo, orderedListingVideoStatus, ordered } = useListingVideoTemplates(
    token,
    listing.id,
  );

  const fmtNoModification = intl.formatMessage({
    id: 'deliver.video_slideshow.noModificationReminder',
  });
  const fmtSelectedTemplate = intl.formatMessage({
    id: 'deliver.video_slideshow.selectedTemplate',
  });
  const fmtSummary = intl.formatMessage({ id: 'deliver.video_slideshow.summary' });
  const fmtBroker = intl.formatMessage({ id: 'deliver.video_slideshow.broker' });
  const fmtFirstname = intl.formatMessage({ id: 'deliver.video_slideshow.firstname' });
  const fmtLastname = intl.formatMessage({ id: 'deliver.video_slideshow.lastname' });
  const fmtEmail = intl.formatMessage({ id: 'deliver.video_slideshow.email' });
  const fmtPhone = intl.formatMessage({ id: 'deliver.video_slideshow.phone' });

  useEffect(() => {
    if (orderedListingVideoStatus === 200 && ordered) {
      displayAlert(ALERT.SUCCESS, intl.formatMessage({ id: 'deliver.photos.success' }));
      dispatch(hideModal(MODAL.SLIDESHOW));
    } else if (orderedListingVideoStatus === 500) {
      displayAlert(ALERT.ERROR, intl.formatMessage({ id: 'deliver.photos.error' }));
    }
  }, [ordered, orderedListingVideoStatus]);

  const onBack = () => {
    setActiveStep(SLIDESHOWMODALSTEP.PICTURES);
  };

  const onConfirm = async () => {
    const picturesWithNewPictureIndex = picturesWithNewOrder.map((picture, index) => {
      picture.picture_index = index;
      picture.included_in_video = true;
      return picture;
    });
    await orderListingVideo(selectedTemplate.id, picturesWithNewPictureIndex);
  };
  const buttons = (
    <Box className={styles.row}>
      <HvButton className={styles.backButton} onClick={onBack}>
        {intl.formatMessage({ id: 'deliver.photos.back' })}
      </HvButton>
      <HvButton onClick={onConfirm}>{intl.formatMessage({ id: 'deliver.photos.submit' })}</HvButton>
    </Box>
  );
  return (
    <Box className={styles.slideshowModalStepper}>
      <Typography className={styles.title}>{fmtSummary}</Typography>
      <Box className={styles.reminderContent}>
        <Typography className={styles.reminderText}>{fmtNoModification}</Typography>
      </Box>
      {selectedTemplate ? (
        <Box className={styles.infoContent}>
          <Typography className={styles.subtitle}>{fmtSelectedTemplate}</Typography>
          <Typography>{`${selectedTemplate[`theme_${theme_lang}`]}`}</Typography>
          {selectedTemplate && (
            <ReactPlayer
              className={styles.videoPlayer}
              url={selectedTemplate.preview_url}
              controls
              width='100%'
              height='auto'
              volume={0.5}
            />
          )}
        </Box>
      ) : null}
      <Box className={styles.infoContent}>
        <Typography className={styles.subtitle}>{fmtBroker}</Typography>
        <img
          width='100'
          height='100'
          src={logo_url || images.user}
          className={styles.logo}
          alt='logo'
        />
        <Typography>{`${fmtFirstname}: ${firstname || ''}`}</Typography>
        <Typography>{`${fmtLastname}: ${lastname || ''}`}</Typography>
        <Typography>{`${fmtEmail}: ${email || ''}`}</Typography>
        <Typography>{`${fmtPhone}: ${phone || ''}`}</Typography>
      </Box>
      {buttons}
    </Box>
  );
}

SlideshowSummary.defaultProps = {
  setActiveStep: _.noop,
};

SlideshowSummary.propTypes = {
  setActiveStep: PropTypes.func,
};

export default SlideshowSummary;
